// Designer session (Pilar F, F1+F2). Holds the document + selection +
// undo/redo, applies ops optimistically and reconciles with the server
// (design_apply_ops). The server is authoritative for rev + inverse ops;
// on conflict we reload. F2: live multi-client co-editing over the doc
// channel — remote ops (from other editors AND the chat/LLM) apply live,
// deduped by client_id + gated by rev (reload on a gap).

import { computed, reactive, ref, shallowRef } from 'vue';
import { callTool } from '../api/client';
import { applyOps, locate, newNodeId, type DesignerDocument, type DesignerNode, type DesignerOp } from './opmodel';
import { connectDocChannel, type DocChannel, type DocOpsMessage, type ChatMessage, type ChatSend } from './docChannel';

/** A remote collaborator's live cursor/selection (F5.3). */
export interface Peer { id: string; by?: string; color: string; x?: number; y?: number; selection?: string | null; label?: string }

interface HistoryEntry { ops: DesignerOp[]; inverse: DesignerOp[] }
export interface DesignerProject { project_id: string; name: string; archived: boolean; can_edit: boolean }

export interface DesignerSessionOpts {
  getTool?: string;   // tool to load the document
  applyTool?: string; // tool to apply ops
  idArg?: string;     // id arg name for the tools
}

/** Editing session for a designer document. Defaults to a design
 *  (design_* tools, design_id); pass component tool names for a
 *  reusable component. */
export function useDesigner(docId: string, opts: DesignerSessionOpts = {}) {
  const getTool = opts.getTool ?? 'design_get_document';
  const applyTool = opts.applyTool ?? 'design_apply_ops';
  const idArg = opts.idArg ?? 'design_id';
  const idOf = (extra: Record<string, unknown>) => ({ [idArg]: docId, ...extra });
  // Stable id for this client so we ignore our own broadcast echo.
  const clientId = newNodeId('client');
  const doc = shallowRef<DesignerDocument | null>(null);
  const project = ref<DesignerProject | null>(null);
  const readOnly = computed(() => !!project.value && (!project.value.can_edit || project.value.archived));
  const selectedId = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const saving = ref(false);
  const liveStatus = ref<'off' | 'open' | 'closed'>('off');
  const onRemoteOps = ref<((msg: DocOpsMessage) => void) | null>(null);
  const peers = reactive<Record<string, Peer>>({});
  // Live shared-chat: a component (DesignerChat) registers a handler to
  // render messages relayed by collaborators into the matching session.
  const onChat = ref<((m: ChatMessage) => void) | null>(null);
  let channel: DocChannel | null = null;
  const undoStack = reactive<HistoryEntry[]>([]);
  const redoStack = reactive<HistoryEntry[]>([]);

  const panels = computed<DesignerNode[]>(() => doc.value?.root.children ?? []);
  const selected = computed<DesignerNode | null>(() => (doc.value && selectedId.value ? locate(doc.value, selectedId.value)?.node ?? null : null));
  const canUndo = computed(() => undoStack.length > 0);
  const canRedo = computed(() => redoStack.length > 0);

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const r = await callTool<{ ok?: boolean; document?: DesignerDocument; project?: DesignerProject | null; error?: string; detail?: string }>(getTool, idOf({}));
      if (!r.document) throw new Error(r.detail ?? r.error ?? 'load failed');
      doc.value = r.document;
      project.value = r.project ?? null;
      undoStack.splice(0); redoStack.splice(0);
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
    } finally {
      loading.value = false;
    }
  }

  /** Apply ops: optimistic locally, then persist. Records undo history. */
  async function apply(ops: DesignerOp[], opts: { record?: boolean } = {}): Promise<boolean> {
    if (!doc.value || readOnly.value || ops.length === 0) return false;
    const prevRev = doc.value.rev;
    const optimistic = applyOps(doc.value, ops);
    doc.value = optimistic; // instant feedback
    saving.value = true;
    error.value = null;
    try {
      const r = await callTool<{ ok?: boolean; rev?: number; inverse?: DesignerOp[]; error?: string; detail?: string }>(
        applyTool,
        idOf({ base_rev: prevRev, ops, client_id: clientId }),
      );
      if (r.error) {
        if (r.error === 'conflict') { await load(); return false; } // someone else edited → resync
        throw new Error(r.detail ?? r.error);
      }
      // Math.max: while this call was in flight, handleRemote may have applied
      // a NEWER rev — writing the older r.rev back would make the next remote
      // op look like a gap and force a needless reload.
      doc.value = { ...doc.value, rev: Math.max(doc.value.rev, r.rev ?? prevRev + 1) };
      if (opts.record !== false && r.inverse) {
        undoStack.push({ ops, inverse: r.inverse });
        redoStack.splice(0);
      }
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      await load(); // rollback to server truth
      return false;
    } finally {
      saving.value = false;
    }
  }

  async function undo(): Promise<void> {
    const entry = undoStack.pop();
    if (!entry || !doc.value) return;
    const prevRev = doc.value.rev;
    doc.value = applyOps(doc.value, entry.inverse);
    const r = await callTool<{ rev?: number; inverse?: DesignerOp[]; error?: string }>(applyTool, idOf({ base_rev: prevRev, ops: entry.inverse, client_id: clientId }));
    if (r.error) { await load(); return; }
    doc.value = { ...doc.value, rev: Math.max(doc.value.rev, r.rev ?? prevRev + 1) };
    redoStack.push({ ops: entry.inverse, inverse: r.inverse ?? entry.ops });
  }

  async function redo(): Promise<void> {
    const entry = redoStack.pop();
    if (!entry || !doc.value) return;
    const prevRev = doc.value.rev;
    doc.value = applyOps(doc.value, entry.ops);
    const r = await callTool<{ rev?: number; inverse?: DesignerOp[]; error?: string }>(applyTool, idOf({ base_rev: prevRev, ops: entry.ops, client_id: clientId }));
    if (r.error) { await load(); return; }
    doc.value = { ...doc.value, rev: Math.max(doc.value.rev, r.rev ?? prevRev + 1) };
    undoStack.push({ ops: entry.ops, inverse: r.inverse ?? entry.ops });
  }

  function select(id: string | null): void { selectedId.value = id; }

  // ── F2 live co-editing ────────────────────────────────────────────
  /** Apply a remote op batch: ignore our own echo (client_id) and dups
   *  (rev ≤ current); apply when it's the next rev; reload on a gap. */
  function handleRemote(msg: DocOpsMessage): void {
    if (!doc.value) return;
    if (msg.client_id && msg.client_id === clientId) return; // our own echo
    if (msg.rev <= doc.value.rev) return; // already have it
    if (msg.rev === doc.value.rev + 1) {
      doc.value = { ...applyOps(doc.value, msg.ops as DesignerOp[]), rev: msg.rev };
      onRemoteOps.value?.(msg);
    } else {
      void load(); // missed an op → resync
    }
  }

  /** Open the live channel: mint a token, subscribe to this doc's room. */
  async function connectLive(): Promise<void> {
    if (channel) return;
    try {
      const r = await callTool<{ token?: string }>('designer_live_token', { doc_id: docId });
      if (!r.token) return; // live is best-effort
      channel = connectDocChannel({
        token: r.token,
        docId,
        onOps: handleRemote,
        onStatus: (s) => { liveStatus.value = s; },
        onPresence: (m) => { peers[m.from] = { id: m.from, by: m.by, color: m.color, x: m.x, y: m.y, selection: m.selection, label: m.label }; },
        onPresenceLeave: (m) => { delete peers[m.from]; },
        onRestore: () => { void load(); }, // version restored elsewhere → resync
        onChat: (m) => { onChat.value?.(m); },
      });
    } catch { /* live optional */ }
  }
  function disconnectLive(): void {
    channel?.close();
    channel = null;
    liveStatus.value = 'off';
    for (const k of Object.keys(peers)) delete peers[k];
  }
  /** Broadcast my cursor/selection to peers (throttle at the call site). */
  function sendPresence(p: { x?: number; y?: number; selection?: string | null; label?: string }): void {
    channel?.sendPresence(p);
  }
  /** Relay a live shared-chat message to collaborators (no-op if offline). */
  function sendChat(m: ChatSend): void {
    channel?.sendChat(m);
  }

  return { doc, project, readOnly, panels, selected, selectedId, loading, saving, error, liveStatus, canUndo, canRedo, peers, load, apply, undo, redo, select, connectLive, disconnectLive, sendPresence, sendChat, onChat, onRemoteOps };
}
